<template>
  <div class="planner-daywise-deenway">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Day Wise Planner</h4>
        <b-badge
          v-show="!loading && !errorMessage"
          variant="light-success"
          class="ml-2"
        >
          {{ sheets.length }} {{ sheets.length === 1 ? "Plan" : "Plans" }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading day wise planner...</p>
    </div>

    <div v-else-if="errorMessage" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ errorMessage }}</h4>
    </div>

    <div v-else-if="!sheets.length" class="text-center py-5 empty-card">
      <p class="mb-0 text-muted">No day wise planner found for the selected filters.</p>
    </div>

    <div v-else>
      <div
        v-for="(sheet, sheetIndex) in sheets"
        :key="sheet.key"
        class="print-page"
        :class="{ 'print-page--break': sheetIndex < sheets.length - 1 }"
      >
        <div class="sheet-top">
          <div class="logo-wrap" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo" />
          </div>
          <h1 class="school-name">{{ schoolName }}</h1>
        </div>

        <table class="meta-table">
          <tbody>
            <tr>
              <th>Grade</th>
              <td>{{ displayValue(sheet.grade || gradeName) }}</td>
              <th>Subject</th>
              <td>{{ displayValue(sheet.subject || subjectName) }}</td>
              <th>Topic</th>
              <td>{{ displayValue(sheet.topic || topicName) }}</td>
            </tr>
            <tr>
              <th>Subtopic</th>
              <td>{{ displayValue(sheet.subtopic || subtopicName) }}</td>
              <th>Duration</th>
              <td>{{ displayValue(sheet.duration) }}</td>
              <th>Date/Day</th>
              <td>{{ displayValue(sheet.dateRange) }}</td>
            </tr>
          </tbody>
        </table>

        <table class="lesson-table">
          <thead>
            <tr>
              <th class="col-org">Lesson Organisation</th>
              <th class="col-activity">Activity</th>
              <th class="col-resource">Resource</th>
              <th class="col-assessment">
                Assessment strategy with Bloom's Taxonomy alignment
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!sheet.rows.length">
              <td colspan="4" class="empty-cell">No lesson activities found.</td>
            </tr>
            <tr v-for="(row, rowIndex) in sheet.rows" :key="`${sheet.key}-row-${rowIndex}`">
              <td class="cell-org" dir="auto" v-html="formatCell(row.lessonOrganisation)"></td>
              <td class="cell-activity" dir="auto" v-html="formatCell(row.activity)"></td>
              <td class="cell-resource" dir="auto" v-html="formatCell(row.resource)"></td>
              <td class="cell-assessment" dir="auto" v-html="formatCell(row.assessmentStrategy)"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BSpinner } from "bootstrap-vue";
import axios from "axios";
import { mapActions } from "vuex";

export default {
  name: "PlannerDayWiseDeenway",
  components: {
    BBadge,
    BSpinner,
  },
  data() {
    return {
      loading: true,
      errorMessage: "",
      db: "",
      campusId: 0,
      classId: 0,
      sectionId: 0,
      subjectId: 0,
      sessionId: 0,
      topicId: 0,
      subtopicId: 0,
      gradeName: "",
      subjectName: "",
      topicName: "",
      subtopicName: "",
      schoolName: "The Deenway School",
      schoolLogo: null,
      sheets: [],
      topicsCache: [],
      originalPageTitle: "",
      autoPrint: false,
    };
  },
  async created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    await this.initReport();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  mounted() {
    if (this.autoPrint) {
      this.$nextTick(() => {
        setTimeout(() => this.print(), 500);
      });
    }
  },
  methods: {
    ...mapActions({ get: "get" }),

    async initReport() {
      const query = this.$route.query || {};
      this.db = query.db || query.dbb || this.$store.state.userData.db;
      this.campusId = Number(
        query.cID || this.$store.state.userData.cId || 0
      );
      this.classId = Number(query.cls || query.classID || 0);
      this.sectionId = Number(query.sec || query.sectionID || 0);
      this.subjectId = Number(query.subjectID || 0);
      this.sessionId = Number(query.sessionID || 0);
      this.topicId = Number(query.topicID || 0);
      this.subtopicId = Number(query.subTopicID || query.subtopicID || 0);
      this.autoPrint = query.autoPrint === "1";

      this.gradeName = query.className || query.gradeName || "";
      this.subjectName = query.subjectName || "";
      this.topicName = query.topicName || "";
      this.subtopicName = query.subtopicName || "";
      this.schoolName =
        query.campusName ||
        this.$store.state.userData?.campusName ||
        this.$store.state.userData?.organizationName ||
        "The Deenway School";

      if (!this.db || !this.classId || !this.subjectId || !this.sessionId) {
        this.errorMessage =
          "Missing required filters. Please close and print again from Day Wise Planner.";
        this.loading = false;
        return;
      }

      try {
        await this.loadCampusInfo();
        await this.resolveNames();
        await this.loadPlannerData();
      } catch (error) {
        console.error("Day wise Deenway print error:", error);
        this.errorMessage = "Failed to load day wise planner report.";
      } finally {
        this.loading = false;
      }
    },

    async loadCampusInfo() {
      try {
        const response = await axios.get(`https://apiv26.myskool.app/api/campuses`, {
          params: { db: this.db, cId: this.campusId },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (campus) => Number(campus.id) === Number(this.campusId)
          );
          if (match) {
            this.schoolName = match.name || this.schoolName;
            this.schoolLogo = match.logo || null;
          }
        }
      } catch (error) {
        console.warn("Campus info fetch failed for day wise print", error);
      }
    },

    async resolveNames() {
      try {
        if (!this.gradeName || !this.subjectName) {
          const classes = await this.get({
            url:
              this.$store.state.domain +
              "myclasses/LoadClasses?db=" +
              this.db +
              "&cId=" +
              this.campusId +
              "&dID=0",
            token: this.$store.state.userData.token,
          });
          const classList = Array.isArray(classes) ? classes : [];
          const selectedClass = classList.find(
            (item) => Number(item.id) === Number(this.classId)
          );
          if (selectedClass && !this.gradeName) {
            this.gradeName = selectedClass.name || "";
          }
        }

        if (!this.subjectName) {
          const subjects = await this.get({
            url:
              this.$store.state.domain +
              "Subjects?db=" +
              this.db +
              "&cId=" +
              this.campusId,
            token: this.$store.state.userData.token,
          });
          const subjectList = Array.isArray(subjects) ? subjects : [];
          const selectedSubject = subjectList.find(
            (item) => Number(item.id) === Number(this.subjectId)
          );
          if (selectedSubject) {
            this.subjectName =
              selectedSubject.subject || selectedSubject.name || "";
          }
        }

        if (this.classId) {
          const topicsPayload = await this.get({
            url:
              this.$store.state.domain +
              "PlannerSheet/LoadTopicsWithSubtopics/" +
              this.campusId +
              "?classID=" +
              this.classId +
              "&sectionID=" +
              this.sectionId +
              "&subjectID=" +
              this.subjectId +
              "&sessionID=" +
              this.sessionId +
              "&campusID=" +
              this.campusId +
              "&db=" +
              this.db,
            token: this.$store.state.userData.token,
          });

          const topics = Array.isArray(topicsPayload)
            ? topicsPayload
            : Array.isArray(topicsPayload?.data)
            ? topicsPayload.data
            : [];

          this.topicsCache = this.normalizeTopicsCache(topics);

          this.topicsCache.forEach((topic) => {
            if (
              !this.topicName &&
              Number(topic.id) === Number(this.topicId)
            ) {
              this.topicName = topic.name || "";
            }
            (topic.subTopics || []).forEach((sub) => {
              if (
                !this.subtopicName &&
                Number(sub.id) === Number(this.subtopicId)
              ) {
                this.subtopicName = sub.name || "";
                if (!this.topicName) {
                  this.topicName = topic.name || "";
                }
              }
            });
          });
        }
      } catch (error) {
        console.warn("Name resolve failed for day wise print", error);
      }
    },

    async loadPlannerData() {
      const response = await this.get({
        url:
          this.$store.state.domain +
          "PlannerDayWise/LoadSelected?db=" +
          this.db +
          "&campusID=" +
          this.campusId +
          "&classID=" +
          this.classId +
          "&sectionID=" +
          this.sectionId +
          "&subjectID=" +
          this.subjectId +
          "&sessionID=" +
          this.sessionId +
          "&mainTopicID=" +
          (this.topicId || 0) +
          "&subtopicID=" +
          (this.subtopicId || 0) +
          "&fromPublished=false",
        token: this.$store.state.userData.token,
        returnData: true,
      });

      const entries = this.normalizeEntries(response);
      if (!entries.length) {
        this.sheets = [];
        return;
      }

      this.sheets = entries.map((entry, index) =>
        this.mapSheet(entry, index)
      );
    },

    normalizeEntries(response) {
      if (response == null) return [];

      if (response.status === "error") return [];

      if (response.status === "success") {
        const raw = response.data;
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === "object") return [raw];
        return [];
      }

      if (Array.isArray(response)) return response;
      if (response.plannerDayWise || response.id) return [response];
      if (response.data && Array.isArray(response.data)) return response.data;
      return [];
    },

    mapSheet(entry, index) {
      const planner = entry.plannerDayWise || entry;
      const details =
        entry.plannerDayWiseDetails ||
        planner.plannerDayWiseDetails ||
        [];

      const { topic, subtopic } = this.resolvePlannerTopicMeta(planner, entry);

      const rows = (Array.isArray(details) ? details : [])
        .map((detail) => ({
          lessonOrganisation:
            detail.lessonOrganisation ||
            detail.LessonOrganisation ||
            detail.lesson_organisation ||
            "",
          activity: detail.activity || detail.Activity || "",
          resource: detail.resource || detail.Resource || "",
          assessmentStrategy:
            detail.assessmentStrategy ||
            detail.AssessmentStrategy ||
            detail.assessment_strategy ||
            "",
        }))
        .filter(
          (row) =>
            row.lessonOrganisation ||
            row.activity ||
            row.resource ||
            row.assessmentStrategy
        );

      return {
        key: `sheet-${planner.id || index}`,
        grade: this.gradeName,
        subject: this.subjectName,
        topic,
        subtopic,
        duration: planner.duration || "",
        dateRange: this.formatDateRange(planner),
        rows,
      };
    },

    normalizeTopicsCache(raw) {
      const source = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
        ? raw.data
        : [];

      return source
        .filter((item) => {
          const topicId = item.mainTopicID ?? item.id;
          const topicName = item.mainTopicName ?? item.name;
          return Number(topicId) > 0 && String(topicName || "").trim();
        })
        .map((item) => ({
          id: item.mainTopicID ?? item.id,
          name: item.mainTopicName ?? item.name ?? "",
          subTopics: (item.subTopics || item.subtopics || []).map((sub) => ({
            id: sub.subTopicID ?? sub.subtopicID ?? sub.id,
            name: sub.subTopicName ?? sub.subtopicName ?? sub.name ?? "",
          })),
        }));
    },

    resolvePlannerTopicMeta(planner = {}, entry = {}) {
      const topicMeta = this.resolveTopicNames(
        planner.mainTopicID || this.topicId,
        planner.subtopicID || planner.subTopicID || this.subtopicId
      );

      return {
        topic:
          planner.meta_mainTopic ||
          entry.meta_mainTopic ||
          planner.mainTopicName ||
          entry.mainTopicName ||
          topicMeta.topic ||
          this.topicName,
        subtopic:
          planner.meta_subTopic ||
          entry.meta_subTopic ||
          planner.subTopicName ||
          planner.subtopicName ||
          entry.subTopicName ||
          entry.subtopicName ||
          topicMeta.subtopic ||
          this.subtopicName,
      };
    },

    resolveTopicNames(mainTopicID, subtopicID) {
      let topic = "";
      let subtopic = "";

      (this.topicsCache || []).forEach((item) => {
        if (Number(item.id) === Number(mainTopicID)) {
          topic = item.name || "";
        }

        (item.subTopics || []).forEach((sub) => {
          if (Number(sub.id) === Number(subtopicID)) {
            subtopic = sub.name || "";
            if (!topic) topic = item.name || "";
          }
        });
      });

      return { topic, subtopic };
    },

    displayValue(value) {
      if (value === null || value === undefined) return "";
      return String(value).trim();
    },

    formatDateRange(planner = {}) {
      const from = this.formatDate(planner.dtFrom);
      const to = this.formatDate(planner.dtTo);
      if (from && to) return `${from}-${to}`;
      if (from) return from;
      if (to) return to;

      const dayFrom = planner.dayFromName || "";
      const dayTo = planner.dayToName || "";
      if (dayFrom && dayTo) return `${dayFrom}-${dayTo}`;
      return dayFrom || dayTo || "";
    },

    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value).split("T")[0] || "";
      }
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const day = String(date.getDate()).padStart(2, "0");
      return `${day}-${months[date.getMonth()]}-${date.getFullYear()}`;
    },

    formatCell(value) {
      if (!value) return "";
      return this.renderCellContent(String(value));
    },

    renderCellContent(text) {
      const normalized = text
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n");

      if (typeof document === "undefined") {
        return this.escapeHtml(normalized).replace(/\n/g, "<br />");
      }

      const container = document.createElement("div");
      container.innerHTML = normalized;

      container.querySelectorAll("a").forEach((anchor) => {
        const href = this.normalizeUrl(anchor.getAttribute("href") || "");
        if (!href) {
          anchor.replaceWith(anchor.textContent || "");
          return;
        }

        const label = this.getLinkLabel(anchor.textContent || "", href);
        anchor.setAttribute("href", href);
        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
        anchor.className = "cell-link";
        anchor.textContent = label;
      });

      this.linkifyPlainUrls(container);

      return container.innerHTML.replace(/\n/g, "<br />");
    },

    linkifyPlainUrls(container) {
      const textNodes = [];
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT
      );

      let node = walker.nextNode();
      while (node) {
        if (!node.parentElement || node.parentElement.tagName !== "A") {
          textNodes.push(node);
        }
        node = walker.nextNode();
      }

      textNodes.forEach((textNode) => {
        const value = textNode.nodeValue || "";
        const urlRegex = /(@?https?:\/\/[^\s<,]+|@?www\.[^\s<,]+)/gi;
        const matches = [...value.matchAll(urlRegex)];
        if (!matches.length) return;

        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        matches.forEach((match) => {
          const rawUrl = match[0];
          const offset = match.index || 0;

          if (offset > lastIndex) {
            fragment.appendChild(
              document.createTextNode(value.slice(lastIndex, offset))
            );
          }

          const href = this.normalizeUrl(
            rawUrl.startsWith("@") ? rawUrl.slice(1) : rawUrl
          );
          const anchor = document.createElement("a");
          anchor.href = href;
          anchor.target = "_blank";
          anchor.rel = "noopener noreferrer";
          anchor.className = "cell-link";
          anchor.textContent = this.getLinkLabel("", href);
          fragment.appendChild(anchor);

          lastIndex = offset + rawUrl.length;
        });

        if (lastIndex < value.length) {
          fragment.appendChild(
            document.createTextNode(value.slice(lastIndex))
          );
        }

        textNode.parentNode.replaceChild(fragment, textNode);
      });
    },

    normalizeUrl(url) {
      const cleaned = String(url || "")
        .trim()
        .replace(/[,.;]+$/g, "");
      if (!cleaned) return "";
      if (/^https?:\/\//i.test(cleaned)) return cleaned;
      return `https://${cleaned}`;
    },

    getLinkLabel(text, href) {
      const cleanText = String(text || "")
        .trim()
        .replace(/[,.;]+$/g, "");
      const cleanHref = String(href || "").trim();

      if (
        cleanText &&
        cleanText !== cleanHref &&
        !cleanText.includes("<")
      ) {
        try {
          const textHost = new URL(this.normalizeUrl(cleanText)).hostname;
          const hrefHost = new URL(cleanHref).hostname;
          if (textHost !== hrefHost) {
            return cleanText;
          }
        } catch (error) {
          return cleanText;
        }
      }

      try {
        const url = new URL(cleanHref);
        return url.hostname.replace(/^www\./, "");
      } catch (error) {
        return cleanText || cleanHref;
      }
    },

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },

    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.planner-daywise-deenway {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
  box-sizing: border-box;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.empty-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.print-page {
  background: #ffffff;
  padding: 1.5rem 1.75rem 2rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1100px;
  margin: 0 auto 1.5rem;
  box-sizing: border-box;
  color: #111;
}

.sheet-top {
  position: relative;
  text-align: center;
  margin-bottom: 0.85rem;
  min-height: 64px;
}

.logo-wrap {
  position: absolute;
  left: 0;
  top: 0;
}

.school-logo {
  height: 58px;
  width: auto;
  object-fit: contain;
}

.school-name {
  margin: 0;
  font-size: 1.7rem;
  font-weight: 800;
  color: #000;
}

.meta-table,
.lesson-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.meta-table th,
.meta-table td,
.lesson-table th,
.lesson-table td {
  border: 1px solid #111;
  vertical-align: top;
  padding: 0.55rem 0.65rem;
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
}

.meta-table th {
  width: 11%;
  background: #f8f9fa;
  font-weight: 700;
  text-align: left;
  color: #111;
}

.meta-table td {
  width: 22.33%;
  background: #fff;
}

.lesson-table {
  margin-top: 0.75rem;
}

.lesson-table th {
  background: #f3f4f6;
  font-weight: 800;
  text-align: left;
  font-size: 0.82rem;
}

.col-org,
.col-activity,
.col-resource,
.col-assessment {
  width: 25%;
}

.cell-org,
.cell-activity,
.cell-resource,
.cell-assessment {
  white-space: pre-wrap;
}

.cell-link {
  color: #2563eb;
  text-decoration: underline;
  word-break: break-word;
}

.cell-link:hover {
  color: #1d4ed8;
}

.empty-cell {
  text-align: center;
  color: #666;
  padding: 1.25rem !important;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .planner-daywise-deenway {
    background: #fff !important;
    padding: 0 !important;
    min-height: 0 !important;
    height: auto !important;
  }

  .print-page {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .print-page--break {
    page-break-after: always;
    break-after: page;
  }

  .school-name {
    font-size: 16pt !important;
  }

  .meta-table th,
  .meta-table td,
  .lesson-table th,
  .lesson-table td {
    font-size: 9pt !important;
    padding: 0.35rem 0.45rem !important;
  }

  .meta-table th,
  .lesson-table th {
    background: #f3f4f6 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .school-logo {
    height: 52px !important;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 landscape;
  margin: 8mm;
}

@media print {
  html,
  body {
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    overflow: visible !important;
  }

  #app,
  .content-wrapper,
  .content-area-wrapper,
  .container,
  .layout-full {
    min-height: 0 !important;
    height: auto !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
}
</style>
